import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Fade,
  Divider,
  Chip,
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import BusinessIcon from '@mui/icons-material/Business';
import StorefrontIcon from '@mui/icons-material/Storefront';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import HistoryIcon from '@mui/icons-material/History';
import PersonIcon from '@mui/icons-material/Person';
import axios from 'axios';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../../../authConfig';

/* ── small helper row ── */
const MetaRow = ({ icon: Icon, label, value }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, py: 1.25 }}>
    <Box
      sx={{
        width: 32, height: 32, borderRadius: 1.5,
        backgroundColor: 'rgba(25,118,210,0.10)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0, mt: 0.1,
      }}
    >
      <Icon sx={{ fontSize: 16, color: 'primary.main' }} />
    </Box>
    <Box>
      <Typography variant="caption" color="text.disabled" display="block" lineHeight={1.2}>
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={600} color="text.primary" lineHeight={1.4}>
        {value || '—'}
      </Typography>
    </Box>
  </Box>
);

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleString('he-IL', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

/* ════════════════════════════════════════════ */
const AccountingManagerEmailTab = ({ invoice }) => {
  const { instance, accounts } = useMsal();
  const [message, setMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const invoiceId = invoice?.id || invoice?._id;

  const fetchHistory = useCallback(async () => {
    if (!invoiceId) return;
    setHistoryLoading(true);
    try {
      const tokenResponse = await instance.acquireTokenSilent({
        ...loginRequest,
        account: accounts[0],
      });
      const { data } = await axios.get(
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/supplierInvoices/${invoiceId}/accounting-email-history`,
        { headers: { Authorization: `Bearer ${tokenResponse.accessToken}` } }
      );
      setHistory(data.messages || []);
    } catch {
      // silently fail — history is non-critical
    } finally {
      setHistoryLoading(false);
    }
  }, [invoiceId, instance, accounts]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const handleSend = async () => {
    if (!message.trim()) return;
    setIsSending(true);
    setResult(null);
    try {
      const tokenResponse = await instance.acquireTokenSilent({
        ...loginRequest,
        account: accounts[0],
      });
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/supplierInvoices/${invoiceId}/send-accounting-email`,
        { message: message.trim() },
        { headers: { Authorization: `Bearer ${tokenResponse.accessToken}` } }
      );
      setResult({ success: true, recipientEmail: response.data.recipientEmail, recipientName: response.data.recipientName });
      setMessage('');
      fetchHistory();
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'שגיאה בשליחת המייל. אנא נסה שוב.';
      setResult({ success: false, message: errorMsg });
    } finally {
      setIsSending(false);
    }
  };

  const invoiceNumber = invoice?.booknum || invoice?.ivnum || invoice?._id;
  const formattedDate = invoice?.ivDate
    ? new Date(invoice.ivDate).toLocaleDateString('he-IL')
    : null;

  return (
    <Box dir="rtl" sx={{ display: 'flex', height: '100%', minHeight: 480, overflow: 'hidden' }}>

      {/* ══════════════════════════════════
          RIGHT PANEL — invoice info
      ══════════════════════════════════ */}
      <Box
        sx={{
          width: 260,
          flexShrink: 0,
          borderLeft: '1px solid',
          borderColor: 'divider',
          background: 'linear-gradient(180deg, #f0f4ff 0%, #f8f9fc 100%)',
          p: 2.5,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Box
            sx={{
              width: 36, height: 36, borderRadius: 2,
              background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 2px 8px rgba(25,118,210,0.30)',
            }}
          >
            <ReceiptLongIcon sx={{ color: '#fff', fontSize: 18 }} />
          </Box>
          <Typography variant="subtitle2" fontWeight={700} color="primary.dark">
            פרטי החשבונית
          </Typography>
        </Box>

        <Divider sx={{ mb: 1.5 }} />

        <MetaRow icon={ReceiptLongIcon} label="מספר חשבונית" value={invoiceNumber} />
        <Divider sx={{ opacity: 0.5 }} />
        <MetaRow icon={StorefrontIcon} label="ספק" value={invoice?.supplierName || invoice?.supName} />
        <Divider sx={{ opacity: 0.5 }} />
        <MetaRow icon={BusinessIcon} label="חברה" value={invoice?.companyName} />
        {formattedDate && (
          <>
            <Divider sx={{ opacity: 0.5 }} />
            <MetaRow icon={CalendarTodayIcon} label="תאריך" value={formattedDate} />
          </>
        )}
        {invoice?.totals?.total && (
          <>
            <Divider sx={{ opacity: 0.5 }} />
            <MetaRow
              icon={AccountBalanceIcon}
              label="סכום"
              value={`₪ ${Number(invoice.totals.total).toLocaleString('he-IL', { minimumFractionDigits: 2 })}`}
            />
          </>
        )}

        <Box sx={{ flex: 1 }} />

        <Box
          sx={{
            mt: 2, p: 1.5, borderRadius: 2,
            backgroundColor: 'rgba(25,118,210,0.07)',
            border: '1px solid rgba(25,118,210,0.15)',
          }}
        >
          <Typography variant="caption" color="primary.dark" lineHeight={1.6} display="block">
            💡 המייל יישלח אוטומטית למנהלת החשבונות המשויכת לחשבונית זו.
          </Typography>
        </Box>
      </Box>

      {/* ══════════════════════════════════
          LEFT PANEL — compose + history
      ══════════════════════════════════ */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* ── Compose area ── */}
        <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
            <EmailIcon sx={{ color: 'primary.main', fontSize: 24 }} />
            <Box>
              <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
                כתיבת הודעה
              </Typography>
              <Typography variant="body2" color="text.secondary">
                שלח הודעה למנהלת החשבונות בנוגע לחשבונית זו
              </Typography>
            </Box>
          </Box>

          <TextField
            value={message}
            onChange={(e) => { setMessage(e.target.value); setResult(null); }}
            multiline
            rows={4}
            placeholder="כתוב כאן את ההודעה..."
            variant="outlined"
            fullWidth
            inputProps={{
              dir: 'rtl',
              style: { textAlign: 'right', fontFamily: 'inherit', lineHeight: 1.7 },
            }}
            sx={{
              mb: 1.5,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2.5,
                backgroundColor: '#fafbff',
                fontSize: 15,
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'primary.main',
                  borderWidth: 2,
                },
              },
            }}
          />

          <Typography variant="caption" color="text.disabled" sx={{ mb: 1.5, display: 'block' }}>
            המייל יכלול את פרטי החשבונית, ההודעה שכתבת ואת שמך.
          </Typography>

          <Fade in={!!result} unmountOnExit>
            <Alert
              severity={result?.success ? 'success' : 'error'}
              icon={
                result?.success
                  ? <CheckCircleOutlineIcon fontSize="inherit" />
                  : <ErrorOutlineIcon fontSize="inherit" />
              }
              sx={{ mb: 1.5, borderRadius: 2, direction: 'rtl', textAlign: 'right' }}
            >
              {result?.success
                ? <>המייל נשלח בהצלחה אל&nbsp;<strong>{result.recipientName || result.recipientEmail}</strong></>
                : result?.message}
            </Alert>
          </Fade>

          <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: 2 }}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              endIcon={
                isSending
                  ? <CircularProgress size={16} color="inherit" />
                  : <SendIcon sx={{ transform: 'scaleX(-1)' }} />
              }
              onClick={handleSend}
              disabled={isSending || !message.trim()}
              sx={{
                borderRadius: 2,
                px: 4,
                py: 1.2,
                fontWeight: 700,
                fontSize: 15,
                background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                boxShadow: '0 3px 10px rgba(25,118,210,0.30)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #1e88e5 0%, #1976d2 100%)',
                  boxShadow: '0 5px 14px rgba(25,118,210,0.40)',
                },
                '&.Mui-disabled': { background: '#e0e0e0', boxShadow: 'none' },
              }}
            >
              {isSending ? 'שולח...' : 'שלח מייל'}
            </Button>

            {message.trim() && (
              <Typography variant="caption" color="text.disabled">
                {message.length} תווים
              </Typography>
            )}
          </Box>
        </Box>

        {/* ── History area ── */}
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            p: 3,
            backgroundColor: '#fafbfc',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <HistoryIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
            <Typography variant="subtitle2" fontWeight={700} color="text.secondary">
              הודעות שנשלחו
            </Typography>
            {!historyLoading && history.length > 0 && (
              <Chip
                label={history.length}
                size="small"
                sx={{ height: 20, fontSize: 11, backgroundColor: 'rgba(25,118,210,0.10)', color: 'primary.main' }}
              />
            )}
          </Box>

          {historyLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          )}

          {!historyLoading && history.length === 0 && (
            <Box
              sx={{
                textAlign: 'center', py: 4,
                border: '1px dashed', borderColor: 'divider',
                borderRadius: 2, color: 'text.disabled',
              }}
            >
              <HistoryIcon sx={{ fontSize: 32, mb: 1, opacity: 0.4 }} />
              <Typography variant="body2">טרם נשלחו הודעות לחשבונית זו</Typography>
            </Box>
          )}

          {!historyLoading && history.map((item, idx) => (
            <Box
              key={idx}
              sx={{
                mb: 2,
                p: 2,
                borderRadius: 2,
                backgroundColor: '#fff',
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
              }}
            >
              {/* header row */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    sx={{
                      width: 28, height: 28, borderRadius: '50%',
                      backgroundColor: 'rgba(25,118,210,0.10)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                  >
                    <PersonIcon sx={{ fontSize: 15, color: 'primary.main' }} />
                  </Box>
                  <Box>
                    <Typography variant="body2" fontWeight={600} lineHeight={1.2}>
                      {item.sentBy || item.sentByEmail}
                    </Typography>
                    {item.sentBy && item.sentByEmail && (
                      <Typography variant="caption" color="text.disabled" lineHeight={1}>
                        {item.sentByEmail}
                      </Typography>
                    )}
                  </Box>
                </Box>
                <Box sx={{ textAlign: 'left' }}>
                  <Typography variant="caption" color="text.disabled" display="block">
                    {formatDate(item.sentAt)}
                  </Typography>
                  {(item.recipientName || item.recipientEmail) && (
                    <Typography variant="caption" color="text.disabled" display="block">
                      → {item.recipientName || item.recipientEmail}
                    </Typography>
                  )}
                </Box>
              </Box>

              <Divider sx={{ mb: 1.5 }} />

              <Typography
                variant="body2"
                sx={{
                  whiteSpace: 'pre-wrap',
                  lineHeight: 1.7,
                  color: 'text.primary',
                  backgroundColor: '#f8f9fc',
                  borderRight: '3px solid',
                  borderColor: 'primary.light',
                  p: 1.5,
                  borderRadius: 1,
                }}
              >
                {item.message}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default AccountingManagerEmailTab;
